import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Upload,
  RefreshCw,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Database,
  Loader2,
  File,
  Circle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Link2,
  Search,
  X,
  HelpCircle,
} from 'lucide-react';
import { docProcessingApi, DocumentInfo, ReindexSummary } from '../../services/docProcessingApi';

const CATEGORIES = ['User_Manuals', 'Trouble-Shooting-Guide', 'SVM_Contracts'];

// ── Typeahead ComboFilter for documents ──────────────────────────────

function DocComboFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
}) {
  const [inputText, setInputText] = useState(value);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setInputText(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (!value && inputText) {
          // Keep typed text as a "contains" filter even without exact selection
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [inputText, value]);

  const filtered = useMemo(() => {
    if (!inputText.trim()) return options;
    const q = inputText.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, inputText]);

  const handleSelect = (opt: string) => {
    onChange(opt);
    setInputText(opt);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputText('');
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);
    setOpen(true);
    // Pass typed text directly as a "contains" filter
    onChange(val);
  };

  const isActive = value.length > 0;

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <div className={`flex items-center border rounded-md overflow-hidden ${isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
        <input
          type="text"
          placeholder={label}
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          className={`text-xs px-2 py-1.5 border-none outline-none bg-transparent w-[120px] ${isActive ? 'text-blue-700' : 'text-gray-600'}`}
        />
        {isActive && (
          <button
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 pr-1.5"
          >
            <X size={11} />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto min-w-[140px] mt-0.5">
          {filtered.slice(0, 30).map((opt) => (
            <div
              key={opt}
              onClick={() => handleSelect(opt)}
              className={`px-2.5 py-1.5 text-xs cursor-pointer hover:bg-gray-100 ${opt === value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
            >
              {opt}
            </div>
          ))}
          {filtered.length > 30 && (
            <div className="px-2.5 py-1 text-[10px] text-gray-400 border-t border-gray-100">
              Type more to narrow ({filtered.length} total)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Upload limits
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES_PER_UPLOAD = 20;
const MAX_TOTAL_BATCH_MB = 200;
const MAX_TOTAL_BATCH_BYTES = MAX_TOTAL_BATCH_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'doc', 'md', 'txt'];

const PAGE_SIZE = 50;

// Reindex phases — matches the backend's actual 3-step flow.
// The backend only exposes (completed, failed, total) via job polling,
// so we cannot show more granular progress than this.
const REINDEX_STEP_IDS = [
  'discover',
  'ingest',
  'bm25_rebuild',
] as const;

const STEP_LABELS: Record<string, string> = {
  discover: 'Discovering documents from storage',
  ingest: 'Ingesting documents (download, chunk, embed, store)',
  bm25_rebuild: 'Rebuilding BM25 search index',
};

interface ReindexStep {
  id: string;
  label: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
}

function buildInitialSteps(): ReindexStep[] {
  return REINDEX_STEP_IDS.map((id) => ({
    id,
    label: STEP_LABELS[id],
    status: 'pending',
  }));
}

type SortField = 'filename' | 'category' | 'type' | 'status' | 'chunks' | 'modules';
type SortDir = 'asc' | 'desc';

// Extracted outside component to avoid re-creation on every render.
function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (sortField !== field) return <ChevronUp size={10} className="text-gray-300" />;
  return sortDir === 'asc'
    ? <ChevronUp size={10} className="text-blue-600" />
    : <ChevronDown size={10} className="text-blue-600" />;
}

export default function DocumentManagement() {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [reindexingSelected, setReindexingSelected] = useState(false);
  const [rebuildingBm25, setRebuildingBm25] = useState(false);
  const [reindexSteps, setReindexSteps] = useState<ReindexStep[]>([]);
  const [reindexSummary, setReindexSummary] = useState<ReindexSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('User_Manuals');
  const [totalGcs, setTotalGcs] = useState(0);
  const [totalIngested, setTotalIngested] = useState(0);
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sort & column filter state
  const [sortField, setSortField] = useState<SortField>('filename');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [globalSearch, setGlobalSearch] = useState('');
  const [colFilterFilename, setColFilterFilename] = useState('');
  const [colFilterCategory, setColFilterCategory] = useState('');
  const [colFilterType, setColFilterType] = useState('');
  const [colFilterStatus, setColFilterStatus] = useState('');
  const [colFilterModules, setColFilterModules] = useState('');
  const [relinking, setRelinking] = useState(false);
  const relinkCancelledRef = useRef(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await docProcessingApi.listDocuments();
      setDocuments(result.documents || []);
      setTotalGcs(result.total_storage_files || 0);
      setTotalIngested(result.total_ingested || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ---- Upload validation ----
  const validateFiles = (files: FileList): string | null => {
    if (files.length > MAX_FILES_PER_UPLOAD) {
      return `Too many files (${files.length}). Maximum ${MAX_FILES_PER_UPLOAD} files per upload.`;
    }

    let totalSize = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop()?.toLowerCase() || '';

      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return `"${file.name}" has unsupported type (.${ext}). Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `"${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum: ${MAX_FILE_SIZE_MB} MB per file.`;
      }
      totalSize += file.size;
    }

    if (totalSize > MAX_TOTAL_BATCH_BYTES) {
      return `Total batch size (${(totalSize / (1024 * 1024)).toFixed(1)} MB) exceeds limit of ${MAX_TOTAL_BATCH_MB} MB.`;
    }

    return null;
  };

  const handleUpload = async (files: FileList) => {
    // Validate first
    const validationError = validateFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    const results: string[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        await docProcessingApi.uploadDocument(files[i], selectedCategory);
        results.push(`${files[i].name} uploaded`);
      } catch (err: any) {
        errors.push(`${files[i].name}: ${err.message}`);
      }
    }

    setUploading(false);
    // Reset file input so re-selecting the same file triggers onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (results.length > 0) {
      setSuccess(`Uploaded ${results.length} file(s) to storage. Run re-index to make them searchable.`);
    }
    if (errors.length > 0) {
      setError(errors.join('; '));
    }
    loadDocuments();
  };

  // ---- Full re-index with job polling ----
  const handleFullReindex = async () => {
    if (!window.confirm(
      'This will delete ALL existing chunks and re-ingest everything from storage. Continue?'
    )) return;

    setReindexing(true);
    setError(null);
    setSuccess(null);
    setReindexSummary(null);
    setReindexSteps(buildInitialSteps());

    try {
      // Step 1: Trigger the reindex — returns 202 with job_id
      setReindexSteps((prev) =>
        prev.map((s) =>
          s.id === 'discover'
            ? { ...s, status: 'in_progress', label: 'Starting full re-index...' }
            : s
        )
      );

      const result = await docProcessingApi.fullReindex();
      const jobId = result.job_id;
      if (!jobId) {
        setError('Re-index failed: no job_id returned');
        setReindexing(false);
        return;
      }

      // Discovery completed (happened before the 202 response)
      setReindexSteps((prev) =>
        prev.map((s) => {
          if (s.id === 'discover') {
            return { ...s, status: 'completed', label: `Discovered ${result.total} documents from storage` };
          }
          if (s.id === 'ingest') {
            return { ...s, status: 'in_progress', label: `Ingesting 0/${result.total} documents...` };
          }
          return s;
        })
      );

      // Step 2: Poll job status until completion, updating progress as we go
      const job = await docProcessingApi.pollJobStatus(jobId, 3000, 600, (progress) => {
        const done = progress.completed + progress.failed;
        const pct = progress.total > 0 ? Math.round((done / progress.total) * 100) : 0;
        setReindexSteps((prev) =>
          prev.map((s) => {
            if (s.id === 'ingest') {
              return {
                ...s,
                status: pct >= 100 ? 'completed' : 'in_progress',
                label: `Ingesting documents... ${done}/${progress.total} (${pct}%)${progress.failed > 0 ? ` — ${progress.failed} failed` : ''}`,
              };
            }
            return s;
          })
        );
      });

      if (job.status === 'completed') {
        // Mark all steps as completed
        setReindexSteps((prev) => prev.map((s) => ({ ...s, status: 'completed' as const })));
        setReindexSummary({
          documents_processed: job.completed,
          pass_rate: job.total > 0 ? job.completed / job.total : 0,
        });
        setSuccess(`Re-index completed: ${job.completed} of ${job.total} documents processed.`);
      } else {
        setError(
          `Re-index finished with failures: ${job.failed} failed out of ${job.total}. ` +
          (job.errors?.length ? job.errors.slice(0, 5).join('; ') : '')
        );
      }
    } catch (err: any) {
      setError(err.message || 'Re-index failed');
    } finally {
      setReindexing(false);
      loadDocuments();
    }
  };

  const handleReindexSelected = async () => {
    if (selectedDocs.size === 0) return;

    const selectedDocsList = documents.filter(d => selectedDocs.has(docKey(d)));
    const gcsPaths = selectedDocsList
      .map(d => d.gcs_path)
      .filter((p): p is string => p != null);

    if (gcsPaths.length === 0) {
      setError('Selected documents have no storage paths. Only storage-backed documents can be re-indexed.');
      return;
    }

    if (!window.confirm(
      `Re-index ${gcsPaths.length} selected document(s)? Their existing chunks will be deleted and re-ingested.`
    )) return;

    setReindexingSelected(true);
    setError(null);
    setSuccess(null);

    try {
      const start = Date.now();
      const result = await docProcessingApi.reindexSelected(gcsPaths);
      if (!result.job_id) {
        setError('Selective re-index failed: no job_id returned');
        setReindexingSelected(false);
        return;
      }

      // Poll until the background job completes
      const job = await docProcessingApi.pollJobStatus(result.job_id);
      const elapsed = ((Date.now() - start) / 1000).toFixed(1);
      if (job.status === 'completed') {
        setSuccess(`Re-indexed ${job.completed} of ${job.total} document(s) (${elapsed}s). Cache invalidated.`);
        setSelectedDocs(new Set());
      } else {
        setError(job.errors?.join('; ') || 'Selective re-index failed');
      }
    } catch (err: any) {
      setError(err.message || 'Selective re-index failed');
    } finally {
      setReindexingSelected(false);
      loadDocuments();
    }
  };

  const handleRebuildBm25 = async () => {
    setRebuildingBm25(true);
    setError(null);
    setSuccess(null);
    try {
      const start = Date.now();
      await docProcessingApi.rebuildBm25();
      const elapsed = ((Date.now() - start) / 1000).toFixed(1);
      setSuccess(`BM25 search index rebuilt successfully (${elapsed}s)`);
    } catch (err: any) {
      setError(err.message || 'BM25 rebuild failed');
    } finally {
      setRebuildingBm25(false);
    }
  };

  const handleRelinkModules = async () => {
    setRelinking(true);
    relinkCancelledRef.current = false;
    setError(null);
    setSuccess(null);
    try {
      const start = Date.now();
      const linkMode = (process.env.REACT_APP_LINK_MODE === 'llm' ? 'llm' : 'keyword') as 'keyword' | 'llm';
      const triggerResult = await docProcessingApi.relinkModules(linkMode);

      if (triggerResult.status === 'already_running') {
        setSuccess('Module linking is already in progress. Check back shortly.');
        setRelinking(false);
        return;
      }

      // Poll for completion with cancellation support and retry limit
      const pollInterval = 3000;
      const maxPollErrors = 10;
      let consecutiveErrors = 0;
      const poll = async (): Promise<void> => {
        if (relinkCancelledRef.current) return; // component unmounted or cancelled
        try {
          const status = await docProcessingApi.getLinkStatus();
          consecutiveErrors = 0; // reset on success
          if (relinkCancelledRef.current) return;
          if (status.status === 'running') {
            const pct = status.total_chunks && status.total_chunks > 0
              ? Math.round(((status.chunks_processed ?? 0) / status.total_chunks) * 100)
              : 0;
            const skippedInfo = (status.skipped ?? 0) > 0 ? `, ${status.skipped} skipped (already linked)` : '';
            setSuccess(
              `Linking in progress: ${status.chunks_processed ?? 0}/${status.total_chunks ?? '?'} chunks (${pct}%), ` +
              `${status.documents_created ?? 0} docs, ${status.links_created ?? 0} links${skippedInfo}...`
            );
            setTimeout(poll, pollInterval);
          } else if (status.status === 'completed') {
            const elapsed = ((Date.now() - start) / 1000).toFixed(1);
            const skippedMsg = (status.skipped ?? 0) > 0 ? `, ${status.skipped} skipped` : '';
            setSuccess(
              `Module linking complete (${elapsed}s): ${status.chunks_processed} chunks processed, ` +
              `${status.documents_created} docs created, ${status.links_created} links created${skippedMsg} ` +
              `(mode: ${status.extraction_mode})` +
              ((status.error_count ?? 0) > 0 ? ` — ${status.error_count} errors` : '')
            );
            setRelinking(false);
            loadDocuments();
          } else {
            setRelinking(false);
          }
        } catch {
          consecutiveErrors++;
          if (consecutiveErrors >= maxPollErrors) {
            setError(`Module linking status polling failed after ${maxPollErrors} retries`);
            setRelinking(false);
            return;
          }
          if (!relinkCancelledRef.current) {
            setTimeout(poll, pollInterval);
          }
        }
      };

      // Start polling after a short delay
      setTimeout(poll, 2000);
    } catch (err: any) {
      setError(err.message || 'Module linking failed');
      setRelinking(false);
    }
  };

  // Cancel polling on unmount
  useEffect(() => {
    return () => {
      relinkCancelledRef.current = true;
    };
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const pendingDocs = documents.filter(d => !d.is_ingested);

  // Derive unique values for dropdown filters
  const uniqueDocuments = useMemo(() => {
    return documents.map((d) => d.filename).sort();
  }, [documents]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set<string>();
    documents.forEach((d) => { if (d.category) cats.add(d.category); });
    return Array.from(cats).sort();
  }, [documents]);

  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    documents.forEach((d) => { if (d.file_type) types.add(d.file_type); });
    return Array.from(types).sort();
  }, [documents]);

  const uniqueModules = useMemo(() => {
    const mods = new Set<string>();
    documents.forEach((d) => (d.linked_modules || []).forEach((m) => mods.add(m)));
    return Array.from(mods).sort();
  }, [documents]);

  const hasAnyFilter = globalSearch.trim() !== '' || colFilterFilename.trim() !== '' || colFilterCategory !== '' || colFilterType !== '' || colFilterStatus !== '' || colFilterModules !== '';

  const clearAllFilters = useCallback(() => {
    setGlobalSearch('');
    setColFilterFilename('');
    setColFilterCategory('');
    setColFilterType('');
    setColFilterStatus('');
    setColFilterModules('');
  }, []);

  // Combined document filter: globalSearch acts as "Document" typeahead,
  // colFilterFilename acts as inline filename filter — both work together


  // Filtered + sorted documents
  const filteredDocs = useMemo(() => {
    let list = documents;

    // Document filter (by filename, contains)
    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      list = list.filter((d) => d.filename.toLowerCase().includes(q));
    }

    if (colFilterFilename.trim()) {
      const q = colFilterFilename.toLowerCase();
      list = list.filter((d) => d.filename.toLowerCase().includes(q));
    }
    if (colFilterCategory) {
      const q = colFilterCategory.toLowerCase();
      list = list.filter((d) => (d.category || '').toLowerCase().includes(q));
    }
    if (colFilterType) {
      const q = colFilterType.toLowerCase();
      list = list.filter((d) => (d.file_type || '').toLowerCase().includes(q));
    }
    if (colFilterStatus) {
      const q = colFilterStatus.toLowerCase();
      list = list.filter((d) => {
        const label = d.is_ingested ? 'ingested' : 'pending';
        return label.includes(q);
      });
    }
    if (colFilterModules) {
      const q = colFilterModules.toLowerCase();
      list = list.filter((d) =>
        (d.linked_modules || []).some((m) => m.toLowerCase().includes(q))
      );
    }

    // Sort
    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'filename':
          cmp = a.filename.localeCompare(b.filename);
          break;
        case 'category':
          cmp = (a.category || '').localeCompare(b.category || '');
          break;
        case 'type':
          cmp = (a.file_type || '').localeCompare(b.file_type || '');
          break;
        case 'status':
          cmp = (a.is_ingested ? 1 : 0) - (b.is_ingested ? 1 : 0);
          break;
        case 'chunks':
          cmp = (a.chunk_count || 0) - (b.chunk_count || 0);
          break;
        case 'modules':
          cmp = (a.linked_modules?.length || 0) - (b.linked_modules?.length || 0);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [documents, globalSearch, colFilterFilename, colFilterCategory, colFilterType, colFilterStatus, colFilterModules, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const paginatedDocs = useMemo(
    () => filteredDocs.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filteredDocs, currentPage]
  );

  // Reset page when filters/docs change
  useEffect(() => {
    setCurrentPage(0);
  }, [documents.length, globalSearch, colFilterFilename, colFilterCategory, colFilterType, colFilterStatus, colFilterModules]);

  // Sort toggle
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };


  // Selection helpers
  const docKey = (d: DocumentInfo) => d.gcs_path ?? `${d.category}-${d.filename}`;

  const toggleDoc = (key: string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const pageKeys = paginatedDocs.map(d => docKey(d));
    const allSelected = pageKeys.every(k => selectedDocs.has(k));
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageKeys.forEach(k => next.delete(k));
      } else {
        pageKeys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  const anyBusy = uploading || reindexing || reindexingSelected || rebuildingBm25 || relinking;

  return (
    <div className="space-y-3">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-red-800">Error</p>
            <p className="text-xs text-red-700 mt-0.5 break-words">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
            <XCircle size={14} />
          </button>
        </div>
      )}
      {success && !reindexing && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700 flex-1">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-600 flex-shrink-0">
            <XCircle size={14} />
          </button>
        </div>
      )}

      {/* Compact stats bar + upload toggle */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <File size={13} className="text-gray-400" />
            <span className="font-medium text-gray-900">{totalGcs}</span>
            Storage Files
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Database size={13} className="text-gray-400" />
            <span className="font-medium text-gray-900">{totalIngested}</span>
            Ingested
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <AlertTriangle size={13} className={pendingDocs.length > 0 ? 'text-yellow-500' : 'text-gray-400'} />
            <span className={`font-medium ${pendingDocs.length > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>
              {pendingDocs.length}
            </span>
            Pending
          </div>
        </div>
        <button
          onClick={() => setUploadExpanded(!uploadExpanded)}
          className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
        >
          <Upload size={13} />
          Upload
          {uploadExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* Collapsible upload section */}
      {uploadExpanded && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">Category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div
            className="p-3"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg py-4 px-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 size={18} className="text-blue-500 animate-spin" />
                  <span className="text-xs text-gray-600">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload size={20} className="text-gray-400" />
                  <p className="text-xs text-gray-600">
                    Drag & drop or click to browse
                  </p>
                  <p className="text-[10px] text-gray-400">
                    PDF, DOCX, DOC, MD, TXT &middot; Max {MAX_FILE_SIZE_MB}MB/file &middot; {MAX_FILES_PER_UPLOAD} files &middot; {MAX_TOTAL_BATCH_MB}MB total
                  </p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.md,.txt"
              className="hidden"
              onChange={(e) => e.target.files && handleUpload(e.target.files)}
            />
          </div>
        </div>
      )}

      {/* Ingestion Actions + Progress Tracker */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Database size={14} />
            Ingestion Actions
          </h2>
          <button
            onClick={loadDocuments}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:bg-gray-100 rounded text-xs transition-colors"
            title="Refresh document list"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleFullReindex}
              disabled={anyBusy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {reindexing ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              {reindexing ? 'Re-indexing...' : 'Full Re-Index'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReindexSelected}
                disabled={anyBusy || selectedDocs.size === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white rounded-md text-xs font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {reindexingSelected ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <RefreshCw size={13} />
                )}
                {reindexingSelected
                  ? 'Re-indexing...'
                  : `Re-Index Selected${selectedDocs.size > 0 ? ` (${selectedDocs.size})` : ''}`}
              </button>
              <div
                className="text-gray-400 hover:text-gray-600 cursor-help transition-colors"
                title="Automatic: Chunks ingestion (parsing, embedding) → BM25 indexing rebuild → Re-Link Modules (Neo4j schema linking) → Cache invalidation. After re-indexing: manually update Frontmatter metadata, then Generate Knowledge Graph for complete document enrichment."
              >
                <HelpCircle size={16} />
              </div>
            </div>
            <button
              onClick={handleRebuildBm25}
              disabled={anyBusy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 border border-gray-300 transition-colors disabled:opacity-50"
            >
              {rebuildingBm25 ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              {rebuildingBm25 ? 'Rebuilding BM25...' : 'Rebuild BM25'}
            </button>
            <button
              onClick={handleRelinkModules}
              disabled={anyBusy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md text-xs font-medium hover:bg-purple-200 border border-purple-300 transition-colors disabled:opacity-50"
            >
              {relinking ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Link2 size={13} />
              )}
              {relinking ? 'Linking Modules...' : 'Re-Link Modules'}
            </button>
          </div>

          {/* Step-by-step progress tracker */}
          {reindexSteps.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">Re-Index Progress</h3>
              <div className="space-y-1.5">
                {reindexSteps.map((step) => (
                  <div key={step.id} className="flex items-center gap-2">
                    {step.status === 'completed' ? (
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    ) : step.status === 'in_progress' ? (
                      <Loader2 size={14} className="text-blue-500 animate-spin flex-shrink-0" />
                    ) : step.status === 'error' ? (
                      <XCircle size={14} className="text-red-500 flex-shrink-0" />
                    ) : (
                      <Circle size={14} className="text-gray-300 flex-shrink-0" />
                    )}
                    <span
                      className={`text-xs ${
                        step.status === 'completed'
                          ? 'text-green-700'
                          : step.status === 'in_progress'
                          ? 'text-blue-700 font-medium'
                          : step.status === 'error'
                          ? 'text-red-700'
                          : 'text-gray-400'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>

              {reindexSummary && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <CheckCircle size={13} className="text-green-600" />
                    <span className="text-xs font-semibold text-green-800">Re-Index Complete</span>
                  </div>
                  <div className="flex gap-4 text-xs text-green-700 mt-1">
                    <div>
                      <span className="font-medium">{reindexSummary.documents_processed}</span> docs processed
                    </div>
                    <div>
                      <span className="font-medium">{Math.round((reindexSummary.pass_rate || 0) * 100)}%</span> success rate
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Documents list with filtering, sorting, and pagination */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <FileText size={14} />
            Documents ({filteredDocs.length})
          </h2>
          <div className="flex items-center gap-2">
            {selectedDocs.size > 0 && (
              <button
                onClick={() => setSelectedDocs(new Set())}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear selection ({selectedDocs.size})
              </button>
            )}
          </div>
        </div>

        {/* Search & filter bar */}
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/70 flex flex-wrap items-center gap-2">
          <DocComboFilter
            label="Document..."
            options={uniqueDocuments}
            value={globalSearch}
            onChange={setGlobalSearch}
          />
          <DocComboFilter
            label="Module..."
            options={uniqueModules}
            value={colFilterModules}
            onChange={setColFilterModules}
          />
          <DocComboFilter
            label="Category..."
            options={uniqueCategories}
            value={colFilterCategory}
            onChange={setColFilterCategory}
          />
          <DocComboFilter
            label="Type..."
            options={uniqueTypes}
            value={colFilterType}
            onChange={setColFilterType}
          />
          <DocComboFilter
            label="Status..."
            options={['ingested', 'pending']}
            value={colFilterStatus}
            onChange={setColFilterStatus}
          />
          {hasAnyFilter && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center">
            <Loader2 size={20} className="text-gray-400 animate-spin mx-auto" />
            <p className="text-xs text-gray-500 mt-1.5">Loading documents...</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-xs">
            {hasAnyFilter
              ? 'No documents match your filters.'
              : 'No documents found. Upload files to get started.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {/* Sortable headers */}
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-2 font-medium w-8">
                      <input
                        type="checkbox"
                        checked={paginatedDocs.length > 0 && paginatedDocs.every(d => selectedDocs.has(docKey(d)))}
                        onChange={toggleAllOnPage}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('filename')}
                    >
                      <span className="inline-flex items-center gap-1">Filename <SortIcon field="filename" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('category')}
                    >
                      <span className="inline-flex items-center gap-1">Category <SortIcon field="category" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('type')}
                    >
                      <span className="inline-flex items-center gap-1">Type <SortIcon field="type" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('status')}
                    >
                      <span className="inline-flex items-center gap-1">Status <SortIcon field="status" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium text-right cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('chunks')}
                    >
                      <span className="inline-flex items-center gap-1">Chunks <SortIcon field="chunks" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('modules')}
                    >
                      <span className="inline-flex items-center gap-1">Linked Modules <SortIcon field="modules" sortField={sortField} sortDir={sortDir} /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocs.map((doc) => {
                    const key = docKey(doc);
                    return (
                      <tr
                        key={key}
                        className={`border-b border-gray-100 hover:bg-gray-50 ${
                          selectedDocs.has(key) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedDocs.has(key)}
                            onChange={() => toggleDoc(key)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900 max-w-xs truncate">
                          {doc.filename}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {doc.category?.replace(/_/g, ' ') || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 uppercase">
                          {doc.file_type || '-'}
                        </td>
                        <td className="px-3 py-2">
                          {doc.is_ingested ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
                              <CheckCircle size={10} />
                              Ingested
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
                              <AlertTriangle size={10} />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">
                          {doc.chunk_count || '-'}
                        </td>
                        <td className="px-3 py-2">
                          {doc.linked_modules && doc.linked_modules.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {doc.linked_modules.map((mod) => (
                                <span
                                  key={mod}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700"
                                  title={mod}
                                >
                                  <Link2 size={8} />
                                  {mod}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-300 text-[10px]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <span className="text-xs text-gray-500">
                  Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={13} />
                    Prev
                  </button>
                  <span className="px-2 py-1 text-xs text-gray-600">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
